import { httpClient } from '@/api/httpClient';
import {
  mapPaginatedResource,
  type PaginatedApiResource,
  type PaginatedResult,
} from '@/api/pagination.types';
import {
  mapApiResourceToPatient,
  mapApiResourceToPatientSummary,
  mapApiResourceToPatientTimelinePage,
  mapPatientOwnerChoice,
  mapPatientWritePayload,
  type Patient,
  type PatientOwnerChoice,
  type PatientApiResource,
  type PatientSummary,
  type PatientSummaryApiResource,
  type PatientTimelinePage,
  type PatientTimelinePageApiResource,
  type PatientWritePayload,
  mapApiResourceToOwnerPet,
  type OwnerPet,
  type OwnerPetApiResource,
} from './patientsApi.types';

export async function fetchClientPatients(clientId: string): Promise<PatientSummary[]> {
  const response = await httpClient.get<PatientSummaryApiResource[]>(
    '/clients/' + clientId + '/patients'
  );
  return response.data.map(mapApiResourceToPatientSummary);
}

export async function fetchWorkspacePatients(
  page: number,
  pageSize: number,
  search: string
): Promise<PaginatedResult<PatientSummary>> {
  const trimmed = search.trim();
  const response = await httpClient.get<PaginatedApiResource<PatientSummaryApiResource>>(
    '/patients',
    { params: { page, page_size: pageSize, search: trimmed.length === 0 ? undefined : trimmed } }
  );
  return mapPaginatedResource(response.data, mapApiResourceToPatientSummary);
}

export async function fetchOwnPatients(): Promise<OwnerPet[]> {
  const response = await httpClient.get<OwnerPetApiResource[]>('/me/pets');
  return response.data.map(mapApiResourceToOwnerPet);
}

export async function fetchPatient(patientId: string): Promise<Patient> {
  const response = await httpClient.get<PatientApiResource>('/patients/' + patientId);
  return mapApiResourceToPatient(response.data);
}

export async function fetchPatientTimeline(
  patientId: string,
  limit: number,
  cursor: string | null
): Promise<PatientTimelinePage> {
  const response = await httpClient.get<PatientTimelinePageApiResource>(
    '/patients/' + patientId + '/timeline',
    { params: { limit, before: cursor === null ? undefined : cursor } }
  );
  return mapApiResourceToPatientTimelinePage(response.data);
}

export async function createWorkspacePatient(
  payload: PatientWritePayload,
  owner: PatientOwnerChoice
): Promise<Patient> {
  const response = await httpClient.post<PatientApiResource>('/patients', {
    ...mapPatientWritePayload(payload),
    owner: owner.kind === 'later' ? null : mapPatientOwnerChoice(owner),
  });
  return mapApiResourceToPatient(response.data);
}

export async function assignPatientOwner(
  patientId: string,
  owner: Exclude<PatientOwnerChoice, { kind: 'later' }>
): Promise<Patient> {
  const response = await httpClient.put<PatientApiResource>(
    '/patients/' + patientId + '/owner',
    mapPatientOwnerChoice(owner)
  );
  return mapApiResourceToPatient(response.data);
}

export async function updatePatient(
  patientId: string,
  payload: PatientWritePayload
): Promise<Patient> {
  const response = await httpClient.patch<PatientApiResource>(
    '/patients/' + patientId,
    mapPatientWritePayload(payload)
  );
  return mapApiResourceToPatient(response.data);
}

export async function updatePatientStatus(patientId: string, status: string): Promise<Patient> {
  const response = await httpClient.patch<PatientApiResource>('/patients/' + patientId, { status });
  return mapApiResourceToPatient(response.data);
}

export async function archivePatient(patientId: string): Promise<Patient> {
  const response = await httpClient.post<PatientApiResource>('/patients/' + patientId + '/archive');
  return mapApiResourceToPatient(response.data);
}

export async function restorePatient(patientId: string): Promise<Patient> {
  const response = await httpClient.post<PatientApiResource>('/patients/' + patientId + '/restore');
  return mapApiResourceToPatient(response.data);
}

export async function uploadPatientPhoto(patientId: string, file: File): Promise<Patient> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await httpClient.post<PatientApiResource>(
    '/patients/' + patientId + '/photo',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return mapApiResourceToPatient(response.data);
}

export async function removePatientPhoto(patientId: string): Promise<Patient> {
  const response = await httpClient.delete<PatientApiResource>('/patients/' + patientId + '/photo');
  return mapApiResourceToPatient(response.data);
}
